import { useState, useRef, useEffect, useContext } from 'react';
import { EditIcon } from 'assets/icons';
import { classnames } from 'helpers/utils';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AppContext, appActions } from 'context';
import { NotificationObject } from 'models/notificationObject';
import { NotificationType } from 'common/enums';
import styles from './album-header-title.module.scss';

interface AlbumHeaderTitleProps {
  title?: string;
  onTitleChange?: (newTitle: string) => void;
  albumId: number;
  editable?: boolean;
  // Saves the title somewhere other than the album's name (e.g. a topic's),
  // with the texts to match. The defaults rename the album.
  save?: (newTitle: string) => Promise<unknown>;
  savedMessage?: string;
  placeholder?: string;
  emptyTitle?: string;
}

export const AlbumHeaderTitle = ({
  title,
  onTitleChange,
  albumId,
  editable = true,
  save,
  savedMessage = 'Album title updated',
  placeholder = 'Enter an album title',
  emptyTitle = 'New album',
}: AlbumHeaderTitleProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  const { dispatch } = useContext(AppContext);

  const handleTitleChange = async (newTitle: string) => {
    try {
      setIsEditMode(false);
      if (save) {
        await save(newTitle);
      } else {
        await VaultsController.updateAlbumMetadata(albumId, {
          name: newTitle,
        });
      }
      onTitleChange?.(newTitle);
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: savedMessage,
          type: NotificationType.Success,
        }),
      });
    } catch (error) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'The title could not be updated',
          message: 'Please try again later.',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTitle(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTitleChange(currentTitle || emptyTitle);
    }
    if (e.key === 'Escape') {
      setCurrentTitle(title);
      setIsEditMode(false);
    }
  };

  const handleBlur = () => {
    handleTitleChange(currentTitle || emptyTitle);
  };

  const handleContainerClick = () => {
    if (editable) {
      setIsEditMode(true);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      const input = inputRef.current;
      input.focus();
      input.selectionStart = input.value.length;
      input.selectionEnd = input.value.length;
    }
  }, [isEditMode]);

  useEffect(() => {
    setCurrentTitle(title);
  }, [title]);

  const containerClassName = classnames(styles.container, {
    [styles.editMode]: isEditMode,
    [styles.notEditable]: !editable,
  });

  const content = (
    <>
      {isEditMode ? (
        <input
          type="text"
          value={currentTitle}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={styles.input}
          ref={inputRef}
        />
      ) : (
        <span
          className={classnames(styles.title, { [styles.placeholder]: !title })}
        >
          {title || 'This is the Album Title'}
        </span>
      )}
      {editable && <EditIcon className={styles.editIcon} />}
    </>
  );

  if (editable) {
    return (
      <button
        type="button"
        className={containerClassName}
        onClick={handleContainerClick}
      >
        {content}
      </button>
    );
  }

  return <div className={containerClassName}>{content}</div>;
};
